'use client';

import { useSuperstars } from '../../providers/queries/superstar';

import debounce from 'lodash.debounce';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Superstar } from '@wwe-portal/entities/superstar.type';
import { useRef } from 'react';
import Link from 'next/link';
import { useElementSize } from '@wwe-portal/ui/hooks/use-element-size';
import {
  RosterFilterData,
  RosterFilterProps,
} from '@wwe-portal/ui/features/roster/roster-filter';
import { RosterAttributeSelect } from '@wwe-portal/ui/features/club-store/components/shop-roster-select';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@wwe-portal/ui/components/popover/popover';
import { PaginationBar } from '@wwe-portal/ui/components/pagination/pagination-bar';
import { cn } from '@wwe-portal/ui/lib/lib';
import {
  SuperstarLayout,
  SuperstarListLayout,
  SuperstarPageLayout,
  SuperstarSkeleton,
} from './superstar-layout';
import Image from 'next/image';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { Button, ClickableDiv } from '@wwe-portal/ui/components/button';
import { SearchInput } from '../../../../../packages/ui/features/search-bar';
import { SearchResultEmpty } from '../../../../../packages/ui/features/search-bar/search-result-empty';

export const SuperstarContent: FC<{
  initialData: Superstar[];
  nPages: number;
}> = ({ initialData, nPages }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { data } = useSuperstars();

  const debounceSearch = useMemo(
    () =>
      debounce((v) => {
        setSearchText(v);
        if (v) setSearchOpen(true);
      }, 500),
    []
  );

  const getFilterDataAction = useCallback((type: keyof RosterFilterData) => {
    return {
      onClearFilter: () =>
        setFilterValue((value) => ({ ...value, [type]: null })),
      onSelectItem: (id: string) =>
        setFilterValue((value) => ({
          ...value,
          [type]: value[type] === id ? null : id,
        })),
    };
  }, []);

  const filterData: RosterFilterProps['filterData'] = useMemo(() => {
    return {
      class: {
        title: 'Class',
        items: [
          {
            id: 'Yellow',
            title: 'showboats',
            iconUrl: '/assets/images/superstar/showboats.webp',
          },
          {
            id: 'Blue',
            title: 'acrobats',
            iconUrl: '/assets/images/superstar/acrobats.webp',
          },
          {
            id: 'Red',
            title: 'powerhouses',
            iconUrl: '/assets/images/superstar/powerhouses.webp',
          },
          {
            id: 'Green',
            title: 'technicians',
            iconUrl: '/assets/images/superstar/technicians.webp',
          },
          {
            id: 'Purple',
            title: 'tricksters',
            iconUrl: '/assets/images/superstar/tricksters.webp',
          },
          {
            id: 'Black',
            title: 'strikers',
            iconUrl: '/assets/images/superstar/strikers.webp',
          },
        ],
        clearFilterText: 'All Classes',
        ...getFilterDataAction('class'),
      },
      era: {
        title: 'Era',
        items: [
          {
            id: 'Attitude',
            title: 'attitude',
            iconUrl: '/assets/images/superstar/attitude.webp',
          },
          {
            id: 'Classic',
            title: 'legends',
            iconUrl: '/assets/images/superstar/legends.webp',
          },
          {
            id: 'Modern',
            title: 'modern',
            iconUrl: '/assets/images/superstar/modern.webp',
          },
          {
            id: 'NewGen',
            title: 'new.generation',
            iconUrl: '/assets/images/superstar/newGeneration.webp',
          },
          {
            id: 'Reality',
            title: 'reality',
            iconUrl: '/assets/images/superstar/reality.webp',
          },
          {
            id: 'PG',
            title: 'pg',
            iconUrl: '/assets/images/superstar/PG.webp',
          },
          {
            id: 'RuthlessAggression',
            title: 'ruthless_aggression',
            iconUrl: '/assets/images/superstar/ruthlessAggression.webp',
          },
          {
            id: 'HallofFame',
            title: 'hall.Of.fame',
            iconUrl: '/assets/images/superstar/hallOfFame.webp',
          },
        ],
        clearFilterText: 'All Eras',
        ...getFilterDataAction('era'),
      },
      stable: {
        title: 'Stable',
        items: [
          {
            id: 'Stable_Alliance',
            title: 'alliance',
            iconUrl: '/assets/images/superstar/Alliance.webp',
          },
          {
            id: 'Stable_Authority',
            title: 'authority',
            iconUrl: '/assets/images/superstar/Authority.webp',
          },
          {
            id: 'Stable_Club',
            title: 'club',
            iconUrl: '/assets/images/superstar/Club.webp',
          },
          {
            id: 'Stable_CORP',
            title: 'corp',
            iconUrl: '/assets/images/superstar/CORP.webp',
          },
          {
            id: 'Stable_DiamondExchange',
            title: 'diamond.exchange',
            iconUrl: '/assets/images/superstar/DiamondExchange.webp',
          },
          {
            id: 'Stable_DX',
            title: 'dx',
            iconUrl: '/assets/images/superstar/DX.webp',
          },
          {
            id: 'Stable_Evolution',
            title: 'evolution',
            iconUrl: '/assets/images/superstar/Evolution.webp',
          },
          {
            id: 'Stable_FourHorsemen',
            title: 'four.horsemen',
            iconUrl: '/assets/images/superstar/FourHorsemen.webp',
          },
          {
            id: 'Stable_HartFoundation',
            title: 'hart.foundation',
            iconUrl: '/assets/images/superstar/HartFoundation.webp',
          },
          {
            id: 'Stable_HeenanFamily',
            title: 'heenan.family',
            iconUrl: '/assets/images/superstar/HeenanFamily.png',
          },
          {
            id: 'Stable_LeagueOfNations',
            title: 'league.of.nations',
            iconUrl: '/assets/images/superstar/LeagueOfNations.webp',
          },
          {
            id: 'Stable_Legacy',
            title: 'legacy',
            iconUrl: '/assets/images/superstar/Legacy.webp',
          },
          {
            id: 'Stable_MillionairesClub',
            title: 'millionaires.club',
            iconUrl: '/assets/images/superstar/MillionairesClub.webp',
          },
          {
            id: 'Stable_Nexus',
            title: 'nexus',
            iconUrl: '/assets/images/superstar/Nexus.png',
          },
          {
            id: 'Stable_NOD',
            title: 'nod',
            iconUrl: '/assets/images/superstar/NOD.png',
          },
          {
            id: 'Stable_NWO',
            title: 'nwo',
            iconUrl: '/assets/images/superstar/NWO.webp',
          },
          {
            id: 'Stable_RatPack',
            title: 'rat.pack',
            iconUrl: '/assets/images/superstar/RatPack.png',
          },
          {
            id: 'Stable_Shield',
            title: 'shield',
            iconUrl: '/assets/images/superstar/Shield.webp',
          },
          {
            id: 'Stable_Z',
            title: 'z',
            iconUrl: '/assets/images/superstar/Z.webp',
          },
        ],
        clearFilterText: 'All Stables',
        ...getFilterDataAction('stable'),
      },
    };
  }, []);

  const clearSearch = () => {
    setSearchText('');
    setInputValue('');
    setSearchOpen(false);
    debounceSearch('');
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      clearSearch();
      return;
    }
    debounceSearch(e.target.value);
    setInputValue(e.target.value);
  };

  const [elementRef, { width }] = useElementSize();
  const ref = useRef<HTMLDivElement>(null);
  const [filterValue, setFilterValue] = useState<RosterFilterData>({
    class: '',
    era: '',
    stable: '',
  });

  const isDirty = useMemo(() => {
    return (
      filterValue.class !== '' ||
      filterValue.era !== '' ||
      filterValue.stable !== '' ||
      searchText !== ''
    );
  }, [filterValue, searchText]);

  const filteredData = useMemo(() => {
    return data?.data.data.filter((item) => {
      const isClassMatch =
        !filterValue.class || item.class?.includes(filterValue.class);
      const isEraMatch =
        !filterValue.era || item.era?.includes(filterValue.era);
      const isStableMatch =
        !filterValue.stable ||
        item.stable?.some(
          (stable) =>
            !filterValue.stable || stable?.includes(filterValue.stable)
        );
      return isClassMatch && isEraMatch && isStableMatch;
    });
  }, [data, filterValue]);

  const filteredDataBySearchValue = useMemo(() => {
    return filteredData?.filter((item) => {
      return (
        item?.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        item?.nickname?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [filteredData, searchText]);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <SuperstarPageLayout
      searchInput={
        <Popover open={searchOpen} modal>
          <PopoverTrigger asChild>
            <div className="w-full">
              <SearchInput
                ref={inputRef}
                onChange={handleChangeSearch}
                placeholder="Search for Roster"
                value={inputValue}
                className="h-[46px] w-full text-sm"
                onBlur={() => setSearchOpen(false)}
                onFocus={() => {
                  if (searchText) setSearchOpen(true);
                }}
                onClear={clearSearch}
              />
            </div>
          </PopoverTrigger>
          <PopoverPortal>
            <PopoverContent
              tabIndex={-1}
              className="overflow-y-auto rounded-md"
              style={{
                maxHeight:
                  'calc(var(--radix-popover-content-available-height) - 40px)',
                width: 'var(--radix-popover-trigger-width)',
              }}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
              }}
              onPointerDownOutside={(e) => {
                setSearchOpen(false);
              }}
              collisionBoundary={
                typeof window !== 'undefined' ? document.body : undefined
              }
            >
              <div className="text-text500 rounded-6 flex flex-col p-2.5 text-sm font-medium">
                {filteredDataBySearchValue?.length ? (
                  filteredDataBySearchValue?.map((item) => (
                    <Link
                      key={item.groupId}
                      href={`/superstar-database/${item.groupId}`}
                      className="rounded-6 hover:bg-back300 p-2.5"
                    >
                      <span>{item.title}</span>
                      <span> &quot;{item.nickname}&quot;</span>
                    </Link>
                  ))
                ) : (
                  <SearchResultEmpty
                    searchKey={searchText}
                    prefix="No superstars found for"
                  />
                )}
              </div>
            </PopoverContent>
          </PopoverPortal>
        </Popover>
      }
      filter={
        <div
          className="text-text300 grid w-full grid-cols-3 overflow-hidden rounded text-center text-sm font-medium"
          ref={elementRef}
        >
          <RosterAttributeSelect
            align="start"
            container={ref.current}
            innerWidth={width}
            selectedId={filterValue.class}
            {...filterData.class}
          />
          <RosterAttributeSelect
            align="center"
            container={ref.current}
            innerWidth={width}
            selectedId={filterValue.era}
            {...filterData.era}
          />
          <RosterAttributeSelect
            align="end"
            container={ref.current}
            innerWidth={width}
            selectedId={filterValue.stable}
            {...filterData.stable}
          />
        </div>
      }
    >
      <SuperstarList
        data={filteredData ?? []}
        isDirty={isDirty}
        initialData={initialData}
        nPages={nPages}
      />
    </SuperstarPageLayout>
  );
};

const PAGE_SIZE = 30;
export const SuperstarList = ({
  data,
  initialData,
  isDirty,
  nPages,
}: {
  data: Superstar[] | undefined;
  initialData?: Superstar[];
  isDirty?: boolean;
  nPages: number;
}) => {
  const [page, setPage] = useState(0);
  const { isMobile } = useResponsive();

  const { displayData, totalPages } = useMemo(() => {
    if (initialData && !isDirty && page === 0) {
      return { displayData: initialData, totalPages: nPages };
    }

    return {
      displayData: data?.slice(
        isMobile ? 0 : page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE
      ),
      totalPages:
        data?.length != null ? Math.ceil(data.length / PAGE_SIZE) : nPages,
    };
  }, [data, initialData, isDirty, page]);

  return (
    <SuperstarListLayout
      pagination={
        <>
          {isMobile ? (
            <Button
              className="text-text600 h-12 w-full border-0 text-sm font-normal md:block"
              variant={'secondary'}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Load More
            </Button>
          ) : (
            <PaginationBar
              totalPages={totalPages}
              page={page}
              setPage={setPage}
              showPages={5}
            />
          )}
        </>
      }
    >
      {!displayData
        ? new Array(PAGE_SIZE)
            .fill(0)
            .map((_, index) => <SuperstarSkeleton key={index} />)
        : displayData.map((item) => {
            return (
              <Link
                key={item.groupId}
                href={`/superstar-database/${item.groupId}`}
              >
                <SuperstarLayout
                  image={
                    <Image
                      fill
                      alt={item.groupId}
                      className="h-full w-full object-fill"
                      src={item.image}
                    />
                  }
                  title={
                    <div className="text-text500 hover:text-green800 mt-2 truncate text-center text-base font-normal">
                      {item.title}
                    </div>
                  }
                  nickName={
                    <ClickableDiv
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                      className="text-text700 mt-1 truncate text-center text-sm font-normal"
                    >
                      {item.nickname}
                    </ClickableDiv>
                  }
                  tier={
                    item?.tier ? (
                      <div className="relative -bottom-0 left-0 right-0 m-auto box-border w-[140px] overflow-hidden">
                        <img
                          src="/assets/images/tier_tag_part_1.webp"
                          alt=""
                          className="box-border w-full border-none align-middle"
                        />
                        <img
                          src="/assets/images/tier_tag_part_new.webp"
                          alt=""
                          className="absolute left-0 right-0 top-0.5 m-auto box-border w-[82%] border-none align-middle"
                        />
                        <div
                          className={cn(
                            'absolute left-0 right-0 top-0.5 m-auto box-border h-[45px] w-[112px] border-l-[22px] border-r-[22px] border-t-[45px] border-transparent',
                            {
                              'border-t-[#c39628]':
                                item.tier === 'S+' || item.tier === 'SPlus',
                              'border-t-[#dc1134]': item.tier === 'S',
                              'border-t-[#417e35]': item.tier === 'B',
                              'border-t-[#b112e8]': item.tier === 'A',
                              'border-t-[#64745f]':
                                item.tier === 'SPlus' || item.tier === 'C',
                            }
                          )}
                        ></div>
                        <p className="absolute inset-0 m-auto mb-0 mt-0 box-border text-center text-sm italic text-white">
                          {item.tier} Tier
                        </p>
                      </div>
                    ) : null
                  }
                />
              </Link>
            );
          })}
    </SuperstarListLayout>
  );
};
